/**
 * Taktische Zeichen nach „Taktische Zeichen im Bevölkerungsschutz – Empfehlungen zur
 * Einführung einer FwDV 102/DV 102“ (BBK, AfKzV-Empfehlung vom 11./12.03.2024).
 * Die Nummern in `quelle` verweisen auf die Kapitel dieser Empfehlung.
 *
 * Aufbau eines Zeichens:
 *  1. Grundzeichen   – Rechteck (Formation), Raute (Person), Fahrzeug (Rechteck mit
 *                      eingezogener Oberkante + Räder), Kreis (Stelle), Haus (Gebäude),
 *                      Dreieck mit Spitze unten (Gefahr rot, Maßnahme blau), „V“ (Drohne)
 *  2. Farbe          – Organisation (Feuerwehr rot, Hilfsorganisationen weiß, THW blau,
 *                      Führung gelb, Polizei grün …), Werte nach Anhang O.1
 *  3. Ergänzungen    – Führungsbalken oben, Versorgungsbalken unten, „Dach“ für ortsfeste Stellen
 *  4. Fachaufgabe    – Sanitätskreuz, Brandbekämpfung, Betreuung (Λ), Orten …
 *  5. Zusatzzeichen  – Stärke (Punkte/Balken über dem Zeichen), Text links oben
 *
 * Alle Zeichen haben dieselbe Zeichenfläche 52 × 40, Mittelpunkt (26, 22).
 */

export type Organisation = 'fw' | 'hiorg' | 'thw' | 'fuehrung' | 'pol' | 'bw' | 'sonst' | 'ziv'

export interface SymbolDef {
  typ: string
  label: string
  /** Für die Leiste gruppiert */
  group: string
  /** Standard-Kennung (Text im Zeichen bzw. Informationstext) */
  standardKennung?: string
  /** Fundstelle in der Empfehlung; „abgeleitet“ = keine eigene Vorgabe, nach den Regeln kombiniert */
  quelle?: string
}

/** Farbwerte nach Anhang O.1 */
export const ORG_FARBE: Record<Organisation, string> = {
  fw: '#FA321E',
  hiorg: '#FFFFFF',
  thw: '#003399',
  fuehrung: '#FAFA00',
  pol: '#64DC32',
  bw: '#B4783C',
  sonst: '#FA8C00',
  ziv: '#BEBEBE',
}
export const ORG_NAME: Record<Organisation, string> = {
  fw: 'Feuerwehr', hiorg: 'Hilfsorganisationen', thw: 'THW', fuehrung: 'Führung, Leitung',
  pol: 'Polizei', bw: 'Bundeswehr', sonst: 'Sonstige Gefahrenabwehr', ziv: 'Zivile Kräfte',
}
const MASSNAHME = '#3264FA'
const GEFAHR = '#FA321E'
const LINIE = '#141414'
const PAPIER = '#FFFFFF'
const SCHRIFT = 'Arial,Helvetica,sans-serif'

/** Linien- und Schriftfarbe auf der jeweiligen Grundfarbe (THW: weiß auf blau) */
const linienFarbe = (org: Organisation) => (org === 'thw' ? '#FFFFFF' : LINIE)

/* ------------------------------------------------------------------ Hilfen */

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const svg = (inhalt: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 40" width="52" height="40" fill="none" stroke-linecap="round" stroke-linejoin="round">${inhalt}</svg>`

const linie = (d: string, farbe = LINIE, b = 1.5) => `<path d="${d}" stroke="${farbe}" stroke-width="${b}"/>`

/** Linie mit weißem Hof – bleibt auf Satellitenbildern lesbar */
const linieHof = (d: string, farbe: string, b = 2) =>
  `<path d="${d}" stroke="#FFFFFF" stroke-opacity=".9" stroke-width="${b + 2.6}"/><path d="${d}" stroke="${farbe}" stroke-width="${b}"/>`

const text = (x: number, y: number, s: string, groesse: number, farbe = LINIE, anker: 'start' | 'middle' | 'end' = 'middle', gewicht = 700) =>
  `<text x="${x}" y="${y}" text-anchor="${anker}" font-family="${SCHRIFT}" font-size="${groesse}" font-weight="${gewicht}" fill="${farbe}">${esc(s)}</text>`

/** Text, der in eine Breite passt (Schriftgröße schrumpft bei langen Kennungen) */
const passText = (x: number, y: number, s: string, breite: number, max: number, farbe = LINIE, anker: 'start' | 'middle' = 'start') => {
  if (!s) return ''
  const groesse = Math.max(4.6, Math.min(max, breite / (s.length * 0.66)))
  return text(x, y, s, Number(groesse.toFixed(1)), farbe, anker)
}

/** Stärkeangabe über dem Zeichen (5.4/5.5): Punkte für Trupp/Gruppe/Zug, Balken für Verbände */
type Staerke = 0 | 1 | 2 | 3 | 'I' | 'II' | 'III'
const staerke = (s: Staerke, yUnten: number, farbe = LINIE) => {
  if (!s) return ''
  if (typeof s === 'number') {
    const abstand = 6
    const start = 26 - ((s - 1) * abstand) / 2
    return Array.from({ length: s }, (_, i) =>
      `<circle cx="${start + i * abstand}" cy="${yUnten - 2.2}" r="1.9" fill="${farbe}" stroke="#FFFFFF" stroke-width=".8"/>`).join('')
  }
  const n = s.length
  const abstand = 5
  const start = 26 - ((n - 1) * abstand) / 2
  return Array.from({ length: n }, (_, i) =>
    `<rect x="${start + i * abstand - 1}" y="${yUnten - 5.5}" width="2" height="5.5" fill="${farbe}" stroke="#FFFFFF" stroke-width=".6"/>`).join('')
}

/* ----------------------------------------------------------- Grundzeichen */

interface Rahmen { x0: number; y0: number; x1: number; y1: number }
const FORMATION: Rahmen = { x0: 8, y0: 11, x1: 44, y1: 35 }
const FAHRZEUG: Rahmen = { x0: 7, y0: 9, x1: 45, y1: 30 }
const cx = 26

/** 1.1 Taktische Formation (Einheit, Dienststelle) */
const formation = (org: Organisation, innen: (r: Rahmen, f: string) => string, opt: { staerke?: Staerke; fuehrung?: boolean; versorgung?: boolean; kennung?: string } = {}) => {
  const r = FORMATION
  const f = linienFarbe(org)
  return svg(
    staerke(opt.staerke ?? 0, r.y0 - 1.2) +
    `<rect x="${r.x0}" y="${r.y0}" width="${r.x1 - r.x0}" height="${r.y1 - r.y0}" fill="${ORG_FARBE[org]}" stroke="${LINIE}" stroke-width="1.6"/>` +
    innen(r, f) +
    (opt.fuehrung ? `<rect x="${r.x0}" y="${r.y0}" width="${r.x1 - r.x0}" height="4" fill="${LINIE}"/>` : '') +
    (opt.versorgung ? `<rect x="${r.x0}" y="${r.y1 - 4}" width="${r.x1 - r.x0}" height="4" fill="${LINIE}"/>` : '') +
    passText(r.x0 + 2, r.y0 + (opt.fuehrung ? 10.5 : 7), opt.kennung ?? '', 22, 6.5, f),
  )
}

/** 1.3 / 5.1.1 Landfahrzeug: eingezogene Oberkante, Räder nach Kategorie 1 */
const fahrzeugForm = (r: Rahmen) => `M${r.x0} ${r.y0} Q${cx} ${r.y0 + 5} ${r.x1} ${r.y0} V${r.y1} H${r.x0} Z`
const fahrzeug = (org: Organisation, innen: (r: Rahmen, f: string) => string, opt: { kennung?: string; fuehrung?: boolean; raeder?: number[]; anhaenger?: boolean; textBreite?: number; textY?: number } = {}) => {
  const r = FAHRZEUG
  const f = linienFarbe(org)
  const raeder = opt.raeder ?? [11.5, 40.5]
  return svg(
    `<path d="${fahrzeugForm(r)}" fill="${ORG_FARBE[org]}" stroke="${LINIE}" stroke-width="1.6"/>` +
    innen(r, f) +
    (opt.fuehrung ? `<path d="M${r.x0} ${r.y0} Q${cx} ${r.y0 + 5} ${r.x1} ${r.y0} V${r.y0 + 4.5} Q${cx} ${r.y0 + 9.5} ${r.x0} ${r.y0 + 4.5} Z" fill="${LINIE}"/>` : '') +
    (opt.anhaenger ? linie(`M${r.x0} ${r.y1 - 3} H${r.x0 - 4} V${r.y1 - 6}`, LINIE, 1.6) : '') +
    raeder.map(x => `<circle cx="${x}" cy="${r.y1 + 3.6}" r="2.9" fill="${PAPIER}" stroke="${LINIE}" stroke-width="1.4"/>`).join('') +
    passText(r.x0 + 2.5, opt.textY ?? r.y0 + (opt.fuehrung ? 13 : 9.5), opt.kennung ?? '', opt.textBreite ?? 26, 7, f),
  )
}

/** 1.2 Person (Raute) */
const RAUTE = { cy: 22, r: 13 }
const rauteForm = `M${cx} ${RAUTE.cy - RAUTE.r} L${cx + RAUTE.r} ${RAUTE.cy} L${cx} ${RAUTE.cy + RAUTE.r} L${cx - RAUTE.r} ${RAUTE.cy} Z`
const person = (fuellung: string, innen: string, opt: { fuehrungskraft?: boolean; sonderfunktion?: boolean; staerke?: Staerke; text?: string } = {}) => {
  const top = RAUTE.cy - RAUTE.r
  return svg(
    staerke(opt.staerke ?? 0, top - 1.5) +
    `<path d="${rauteForm}" fill="${fuellung}" stroke="${LINIE}" stroke-width="1.6"/>` +
    (opt.fuehrungskraft ? `<path d="M${cx} ${top} L${cx + 5.5} ${top + 5.5} H${cx - 5.5} Z" fill="${LINIE}"/>` : '') +
    (opt.sonderfunktion ? linie(`M${cx - 5.5} ${top + 5.5} H${cx + 5.5}`, LINIE, 1.6) : '') +
    innen +
    (opt.text ? passText(cx, RAUTE.cy + 3.4, opt.text, 17, 7.5, LINIE, 'middle') : ''),
  )
}

/** 1.6 Stelle, Platz, Einrichtung (Kreis); 3.7 „Dach“ für temporär ortsfeste Stellen */
const KREIS = { cy: 23, r: 12.5 }
const stelle = (fuellung: string, innen: string, opt: { dach?: boolean; kennung?: string } = {}) =>
  svg(
    `<circle cx="${cx}" cy="${KREIS.cy}" r="${KREIS.r}" fill="${fuellung}" stroke="${LINIE}" stroke-width="1.6"/>` +
    (opt.dach ? linie(`M${cx - 13} ${KREIS.cy - 7} L${cx} ${KREIS.cy - KREIS.r - 6} L${cx + 13} ${KREIS.cy - 7}`, LINIE, 1.6) : '') +
    innen +
    (opt.kennung ? passText(3, 8, opt.kennung, 14, 6.5) : ''),
  )

/** 1.7 Gebäude */
const gebaeude = (fuellung: string, innen: string) =>
  svg(`<path d="M9 16 L26 6 L43 16 V35 H9 Z" fill="${fuellung}" stroke="${LINIE}" stroke-width="1.6"/>` + linie('M9 16 H43', LINIE, 1.4) + innen)

/** 1.11 Gefahr (Dreieck mit Spitze unten und Grundlinie oben, rot) */
const gefahr = (innen: string) =>
  svg(`<path d="M7.5 5 H44.5 L26 36 Z" fill="#FFFFFF" fill-opacity=".92" stroke="${GEFAHR}" stroke-width="2"/>` + innen)

/** 1.10 Maßnahme (Dreieck mit Spitze unten, blau) */
const massnahme = (innen: string) =>
  svg(`<path d="M7.5 6 H44.5 L26 37 Z" fill="#FFFFFF" fill-opacity=".92" stroke="${MASSNAHME}" stroke-width="2"/>` + innen)

/* ------------------------------------------------- Fachaufgaben-Zeichen */

/** 4.6.1 Sanität: Kreuz über die ganze Fläche */
const sanKreuz = (r: Rahmen, f: string, mitte = (r.y0 + r.y1) / 2) =>
  linie(`M${r.x0} ${mitte} H${r.x1} M${cx} ${r.y0} V${r.y1}`, f, 1.4)
/** 4.6.3 Rettungswesen: Kreuz + kurzer senkrechter Strich rechts */
const rettKreuz = (r: Rahmen, f: string, mitte = (r.y0 + r.y1) / 2) =>
  sanKreuz(r, f, mitte) + linie(`M${r.x1 - 7} ${mitte - 4.5} V${mitte + 4.5}`, f, 1.4)
/** 4.6.4 Arztwesen: Kreuz + kurzer waagerechter Strich unten */
const arztKreuz = (r: Rahmen, f: string, mitte = (r.y0 + r.y1) / 2) =>
  sanKreuz(r, f, mitte) + linie(`M${cx - 4.5} ${mitte + (r.y1 - mitte) * 0.55} H${cx + 4.5}`, f, 1.4)
/** 4.6.5 Patiententransport: Kreis mit Speichen */
const speichenrad = (x: number, y: number, rad: number, f: string) => {
  const d = rad * Math.SQRT1_2
  return `<circle cx="${x}" cy="${y}" r="${rad}" fill="none" stroke="${f}" stroke-width="1.3"/>` +
    linie(`M${x - rad} ${y} H${x + rad} M${x} ${y - rad} V${y + rad} M${x - d} ${y - d} L${x + d} ${y + d} M${x - d} ${y + d} L${x + d} ${y - d}`, f, 1.1)
}
/** 4.3.1 Brandbekämpfung: Strahlrohr-Linie mit Gabel */
const brand = (r: Rahmen, f: string) => {
  const m = (r.y0 + r.y1) / 2 + 1
  return linie(`M${r.x0} ${m} H${r.x1} M${r.x1} ${r.y0 + 2} L${r.x1 - 11} ${m} L${r.x1} ${r.y1}`, f, 1.5)
}
/** 4.2.1 Betreuung: Λ */
const betreuungsDach = (r: Rahmen, f: string) => linie(`M${r.x0} ${r.y1} L${cx} ${r.y0} L${r.x1} ${r.y1}`, f, 1.5)
/** 3.4 Drohne: stilisiertes V; 5.1.4.2 Drehflügler: Fliege */
const drohneV = (x: number, y: number, s: number, f: string) =>
  `<path d="M${x - 10 * s} ${y - 3.5 * s} L${x} ${y + 2.5 * s} L${x + 10 * s} ${y - 3.5 * s} L${x + 10 * s} ${y - 0.5 * s} L${x} ${y + 5.8 * s} L${x - 10 * s} ${y - 0.5 * s} Z" fill="${f}"/>`
const drehfluegler = (x: number, y: number, s: number, f: string) =>
  `<path d="M${x - 7 * s} ${y - 3 * s} L${x} ${y} L${x - 7 * s} ${y + 3 * s} Z M${x + 7 * s} ${y - 3 * s} L${x} ${y} L${x + 7 * s} ${y + 3 * s} Z" fill="${f}"/>`
/** 4.4.2 Orten, biologisch (Rettungshund) */
const hund = (x: number, y: number, s: number, f: string) =>
  linie(
    `M${x - 9 * s} ${y - 8 * s} L${x - 5 * s} ${y} M${x - 5 * s} ${y} H${x + 5 * s} ` +
    `M${x - 5 * s} ${y} L${x - 8 * s} ${y + 7 * s} M${x - 5 * s} ${y} L${x - 2 * s} ${y + 7 * s} ` +
    `M${x + 5 * s} ${y} L${x + 2 * s} ${y + 7 * s} M${x + 5 * s} ${y} L${x + 8 * s} ${y + 7 * s} ` +
    `M${x + 5 * s} ${y} L${x + 8 * s} ${y - 7 * s} L${x + 11 * s} ${y} Z`, f, 1.4)
/** 4.4.1 Erkunden: Schrägstrich */
const erkunden = (f: string) => linie('M16 30 L36 12', f, 1.8)
/** Versorgung/Verpflegung (F.1.17): „Kochtopf“-Kreis mit Kerbe */
const verpflegung = (f: string) =>
  `<path d="M30.5 18.5 A6 6 0 1 0 30.5 25.5" stroke="${f}" stroke-width="1.6"/><path d="M30.5 18.5 L26 22 L30.5 25.5" stroke="${f}" stroke-width="1.6"/>`
/** 4.7.16 Sicherheit: Schild */
const schild = (f: string, dx = 0) => linie(`M${20 + dx} 15 Q${26 + dx} 13 ${32 + dx} 15 V22 Q${32 + dx} 28 ${26 + dx} 31 Q${20 + dx} 28 ${20 + dx} 22 Z`, f, 1.5)
/** Doppelpfeil ↔ */
const doppelpfeil = (x0: number, x1: number, y: number, f: string) =>
  linie(`M${x0} ${y} H${x1} M${x0 + 3} ${y - 2.5} L${x0} ${y} L${x0 + 3} ${y + 2.5} M${x1 - 3} ${y - 2.5} L${x1} ${y} L${x1 - 3} ${y + 2.5}`, f, 1.3)

/* -------------------------------------------- Gefahren und Schäden (5.8) */

const zerstoerung = (n: 1 | 2 | 3) => {
  const versatz = n === 1 ? [0] : n === 2 ? [-3.2, 3.2] : [-6, 0, 6]
  return svg(versatz.map(v => linieHof(`M${15 + v} 11 L${37 + v} 33 M${37 + v} 11 L${15 + v} 33`, GEFAHR, 1.8)).join(''))
}
const brandStufe = (n: 1 | 2 | 3) => {
  const breite = 8
  const start = 26 - (n * breite) / 2
  return svg(Array.from({ length: n }, (_, i) => {
    const x = start + i * breite
    return linieHof(`M${x} 34 L${x + breite} 10 V34 Z`, GEFAHR, 1.8)
  }).join(''))
}
const befahrbarkeit = (n: 2 | 4) => {
  const abstand = n === 2 ? 6 : 4.6
  const start = 26 - ((n - 1) * abstand) / 2
  return svg(Array.from({ length: n }, (_, i) => linieHof(`M${start + i * abstand} 9 V35`, LINIE, 1.8)).join(''))
}

/* --------------------------------------------------------------- Personen */

const top = RAUTE.cy - RAUTE.r
const unten = RAUTE.cy + RAUTE.r

/* ======================================================================== *
 *  KATALOG
 * ======================================================================== */

export const SYMBOL_KATALOG: SymbolDef[] = [
  /* Fahrzeuge */
  { typ: 'elw1', label: 'Einsatzleitwagen 1 (ELW 1)', group: 'Fahrzeuge', standardKennung: 'ELW 1', quelle: 'C.2.2' },
  { typ: 'rtw', label: 'Rettungswagen (RTW)', group: 'Fahrzeuge', standardKennung: 'RTW', quelle: 'F.2.3' },
  { typ: 'ktw', label: 'Krankentransportwagen (KTW)', group: 'Fahrzeuge', standardKennung: 'KTW', quelle: 'F.2.1' },
  { typ: 'nef', label: 'Notarzteinsatzfahrzeug (NEF)', group: 'Fahrzeuge', standardKennung: 'NEF', quelle: 'F.2.4' },
  { typ: 'gw_drohne', label: 'GW-Drohne', group: 'Fahrzeuge', standardKennung: 'GW-Drohne', quelle: 'abgeleitet (5.1.1 + 3.4)' },
  { typ: 'gw_hundestaffel', label: 'GW-Rettungshunde', group: 'Fahrzeuge', standardKennung: 'GW-Hund', quelle: 'abgeleitet (5.1.1 + 4.4.2)' },
  { typ: 'drohne', label: 'Drohne (Drehflügler)', group: 'Fahrzeuge', standardKennung: '', quelle: '3.4 + 5.1.4.2' },

  /* Einheiten */
  { typ: 'drohnentrupp', label: 'Drohnentrupp', group: 'Einheiten', standardKennung: '', quelle: 'F.1.16' },
  { typ: 'rettungshund', label: 'Rettungshundetrupp', group: 'Einheiten', standardKennung: '', quelle: 'abgeleitet (1.1 + 4.4.2)' },
  /* Technischer Typ bleibt 'sanitaetsgruppe' (gespeicherte Karten), gezeichnet und benannt als Sanitätstrupp */
  { typ: 'sanitaetsgruppe', label: 'Sanitätstrupp', group: 'Einheiten', standardKennung: '', quelle: 'abgeleitet (1.1 + 4.6.1 + 5.4.1)' },

  /* Stellen und Einrichtungen */
  { typ: 'bereitstellungsraum', label: 'Bereitstellungsraum', group: 'Einrichtungen', standardKennung: '', quelle: 'D.2.1' },
  { typ: 'meldekopf', label: 'Meldekopf', group: 'Einrichtungen', standardKennung: '', quelle: 'D.2.3' },
  { typ: 'landeplatz', label: 'Hubschrauberlandezone', group: 'Einrichtungen', standardKennung: '', quelle: 'D.2.6' },
  { typ: 'drohnen_startplatz', label: 'Start- und Landeplatz Drohne', group: 'Einrichtungen', standardKennung: '', quelle: 'abgeleitet (D.2.6 + 3.4)' },
  { typ: 'patientenablage', label: 'Patientenablage', group: 'Einrichtungen', standardKennung: '', quelle: 'F.3.1' },
  { typ: 'unfallhilfstelle', label: 'Unfallhilfsstelle / Sanitätsstation', group: 'Einrichtungen', standardKennung: 'UHS', quelle: 'F.3.3' },
  { typ: 'behandlungsplatz', label: 'Behandlungsplatz', group: 'Einrichtungen', standardKennung: '', quelle: 'F.3.5' },
  { typ: 'rettungsmittelhalteplatz', label: 'Rettungsmittelhalteplatz', group: 'Einrichtungen', standardKennung: '', quelle: 'F.3.11' },
  { typ: 'anlaufstelle', label: 'Anlaufstelle für Betroffene', group: 'Einrichtungen', standardKennung: '', quelle: 'F.3.12' },
  { typ: 'betreuungsplatz', label: 'Betreuungsplatz', group: 'Einrichtungen', standardKennung: '', quelle: 'F.3.14' },

  /* Gefahren und Schäden */
  { typ: 'vermutete_gefahr', label: 'Vermutete Gefahr', group: 'Schäden & Gefahren', quelle: '5.8.1.13' },
  { typ: 'akute_gefahr', label: 'Akute Gefahr', group: 'Schäden & Gefahren', quelle: '5.8.1.14' },
  { typ: 'gefaehrliche_stoffe', label: 'Gefährliche Stoffe', group: 'Schäden & Gefahren', quelle: '5.8.1.7' },
  { typ: 'explosionsgefahr', label: 'Explosionsgefahr', group: 'Schäden & Gefahren', quelle: '5.8.1.11' },
  { typ: 'elektrische_energie', label: 'Elektrische Energie', group: 'Schäden & Gefahren', quelle: '5.8.1.9' },
  { typ: 'gefahr_atomar', label: 'Radioaktive Stoffe (A)', group: 'Schäden & Gefahren', quelle: '4.1.6' },
  { typ: 'gefahr_biologisch', label: 'Biologische Stoffe (B)', group: 'Schäden & Gefahren', quelle: '4.1.7' },
  { typ: 'gefahr_chemisch', label: 'Chemische Stoffe (C)', group: 'Schäden & Gefahren', quelle: '4.1.8' },
  { typ: 'ueberschwemmung', label: 'Überschwemmtes Gebiet', group: 'Schäden & Gefahren', quelle: '5.8.1.5' },
  { typ: 'entstehungsbrand', label: 'Entstehungsbrand', group: 'Schäden & Gefahren', quelle: '5.8.5.1' },
  { typ: 'fortentwickelter_brand', label: 'Fortentwickelter Brand', group: 'Schäden & Gefahren', quelle: '5.8.5.2' },
  { typ: 'vollbrand', label: 'Vollbrand', group: 'Schäden & Gefahren', quelle: '5.8.5.3' },
  { typ: 'angeschlagen', label: 'Angeschlagen / beschädigt', group: 'Schäden & Gefahren', quelle: '5.8.4.1' },
  { typ: 'teilzerstoert', label: 'Teilzerstört', group: 'Schäden & Gefahren', quelle: '5.8.4.2' },
  { typ: 'zerstoert', label: 'Total zerstört', group: 'Schäden & Gefahren', quelle: '5.8.4.3' },
  { typ: 'gesperrt', label: 'Gesperrt (für Einsatzkräfte befahrbar)', group: 'Schäden & Gefahren', quelle: '5.8.9.1' },
  { typ: 'schwer_befahrbar', label: 'Schwierig befahrbar / teilblockiert', group: 'Schäden & Gefahren', quelle: '5.8.9.3' },
  { typ: 'nicht_befahrbar', label: 'Nicht befahrbar / blockiert', group: 'Schäden & Gefahren', quelle: '5.8.9.4' },

  /* Personen */
  { typ: 'person', label: 'Person, unverletzt', group: 'Personen', quelle: '5.8.8.1' },
  { typ: 'betroffen', label: 'Betroffene Person', group: 'Personen', quelle: '5.8.8.2' },
  { typ: 'verletzt', label: 'Verletzte Person', group: 'Personen', quelle: '5.8.8.3' },
  { typ: 'tot', label: 'Tote Person', group: 'Personen', quelle: '5.8.8.7' },
  { typ: 'vermisst', label: 'Vermisste Person', group: 'Personen', quelle: '5.8.8.8' },
  { typ: 'in_wassergefahr', label: 'Person in Wassergefahr', group: 'Personen', quelle: '5.8.8.9' },
  { typ: 'verschuettet', label: 'Person in Zwangslage (verschüttet, eingeklemmt)', group: 'Personen', quelle: '5.8.8.10' },
  { typ: 'gerettet', label: 'Gerettete / befreite Person', group: 'Personen', quelle: '5.8.8.11' },
]

/**
 * Nicht mehr wählbar (Vereinfachung), nur noch für bereits gespeicherte Karten und
 * zugeteilte Fahrzeuge: Label, Standard-Kennung und Quelle werden weiter gefunden.
 */
const ALTE_SYMBOLE: SymbolDef[] = [
  { typ: 'erkundung', label: 'Maßnahme: Erkunden', group: 'Schäden & Gefahren', quelle: '1.10 + 4.4.1' },
  { typ: 'leitstelle', label: 'Leitstelle', group: 'Einrichtungen', standardKennung: '', quelle: 'D.2.5' },
  { typ: 'krankenhaus', label: 'Krankenhaus', group: 'Einrichtungen', standardKennung: '', quelle: 'F.3.16' },
  { typ: 'hubschrauber', label: 'Hubschrauber', group: 'Fahrzeuge', standardKennung: '', quelle: '1.4 + 5.1.4.2' },
  { typ: 'elw2', label: 'Einsatzleitwagen 2 (ELW 2)', group: 'Fahrzeuge', standardKennung: 'ELW 2', quelle: 'C.2.3' },
  { typ: 'loeschfahrzeug', label: 'Löschfahrzeug (LF)', group: 'Fahrzeuge', standardKennung: 'LF', quelle: 'C.2.7' },
  { typ: 'einsatzfahrzeug', label: 'Einsatzfahrzeug (allgemein)', group: 'Fahrzeuge', standardKennung: '', quelle: '5.1.1.1' },
  { typ: 'abrollbehaelter', label: 'Abrollbehälter', group: 'Fahrzeuge', standardKennung: 'AB', quelle: '5.1.3.1' },
  { typ: 'anhaenger', label: 'Anhänger', group: 'Fahrzeuge', standardKennung: 'Anh', quelle: '5.1.2.4' },
  { typ: 'wasserfahrzeug', label: 'Wasserfahrzeug', group: 'Fahrzeuge', standardKennung: 'Boot', quelle: '1.5' },
  { typ: 'rettungsdienst', label: 'Rettungsdienst', group: 'Einheiten', standardKennung: '', quelle: 'F.1.11' },
  { typ: 'aerztliche_versorgung', label: 'Arzttrupp', group: 'Einheiten', standardKennung: '', quelle: 'F.1.15' },
  { typ: 'formation_einsatz', label: 'Einsatzeinheit', group: 'Einheiten', standardKennung: '', quelle: 'F.1.4' },
  { typ: 'betreuung', label: 'Betreuung', group: 'Einheiten', standardKennung: '', quelle: '4.2.1' },
  { typ: 'psnv', label: 'Psychosoziale Notfallversorgung', group: 'Einheiten', standardKennung: '', quelle: '4.2.2' },
  { typ: 'verpflegung', label: 'Verpflegung', group: 'Einheiten', standardKennung: '', quelle: 'F.1.17' },
  { typ: 'technik_sicherheit', label: 'Technik und Sicherheit', group: 'Einheiten', standardKennung: 'T&S', quelle: 'abgeleitet (1.1 + 4.7.16)' },
  { typ: 'brandschutz', label: 'Brandschutz / Feuerwehr', group: 'Einheiten', standardKennung: '', quelle: '4.3.1' },
  { typ: 'einsatzleitung', label: 'Einsatzleitung', group: 'Führung', standardKennung: 'EL', quelle: 'D.1.4' },
  { typ: 'befehlsstelle', label: 'Befehlsstelle / Führungsstelle', group: 'Führung', standardKennung: 'BSt', quelle: 'D.1.1' },
  { typ: 'einsatzleiter', label: 'Einsatzleiter', group: 'Führung', standardKennung: 'EL', quelle: 'D.3.2' },
  { typ: 'lna', label: 'Leitender Notarzt (LNA)', group: 'Führung', standardKennung: 'LNA', quelle: 'D.3.3' },
  { typ: 'orgl', label: 'Organisatorischer Leiter (OrgL)', group: 'Führung', standardKennung: 'OrgL', quelle: 'D.3.4' },
  { typ: 'truppfuehrer', label: 'Truppführer', group: 'Führung', standardKennung: '', quelle: 'D.3 + 5.4.1' },
  { typ: 'gruppenfuehrer', label: 'Gruppenführer', group: 'Führung', standardKennung: '', quelle: 'D.3 + 5.4.3' },
  { typ: 'zugfuehrer', label: 'Zugführer', group: 'Führung', standardKennung: '', quelle: 'D.3.7' },
  { typ: 'verbandsfuehrer', label: 'Verbandsführer', group: 'Führung', standardKennung: '', quelle: 'D.3 + 5.5.1' },
  { typ: 'arzt', label: 'Arzt', group: 'Führung', standardKennung: '', quelle: '1.2 + 4.6.4' },
  { typ: 'berater', label: 'Fachberater / Sonderfunktion', group: 'Führung', standardKennung: 'Fb', quelle: '3.2' },
]

/** Definition eines Typs aus aktuellem oder altem Katalog (zum Nachschlagen, nicht für Paletten) */
export function symbolDef(typ: string): SymbolDef | undefined {
  return SYMBOL_KATALOG.find(s => s.typ === typ) ?? ALTE_SYMBOLE.find(s => s.typ === typ)
}

/* ======================================================================== *
 *  HAUPTFUNKTION
 * ======================================================================== */

/** Organisation, in deren Farbe ein Typ ohne ausdrückliche Angabe gezeichnet wird */
const STANDARD_ORG: Record<string, Organisation> = {
  loeschfahrzeug: 'fw', brandschutz: 'fw',
  einsatzleitung: 'fuehrung', befehlsstelle: 'fuehrung', einsatzleiter: 'fuehrung', lna: 'fuehrung', orgl: 'fuehrung',
  bereitstellungsraum: 'hiorg', meldekopf: 'hiorg', leitstelle: 'fuehrung', landeplatz: 'hiorg',
}

const WEISS_GEFUELLT = new Set(['bereitstellungsraum', 'meldekopf', 'landeplatz'])

export function symbolSvg(typ: string, kennung?: string, organisation?: Organisation): string {
  const org: Organisation = organisation ?? STANDARD_ORG[typ] ?? 'hiorg'
  const k = kennung ?? symbolDef(typ)?.standardKennung ?? ''
  /* Bereitstellungsraum, Meldekopf und Hubschrauberlandeplatz sind immer weiß gefüllt (schwarze Kontur/Schrift),
     auch bei bereits platzierten Zeichen mit anderer Organisationsfarbe */
  const fuell = WEISS_GEFUELLT.has(typ) ? PAPIER : ORG_FARBE[org]

  switch (typ) {
    /* --- Fahrzeuge --- */
    case 'elw1':
    case 'elw2':
      return fahrzeug(org, () => '', { kennung: k, fuehrung: true })
    case 'loeschfahrzeug': return fahrzeug(org, brand, { kennung: k })
    case 'rtw': return fahrzeug(org, (r, f) => rettKreuz(r, f, 22), { kennung: k, textBreite: 16, textY: 19.5 })
    case 'ktw': return fahrzeug(org, (r, f) => sanKreuz(r, f, 22), { kennung: k, textBreite: 16, textY: 19.5 })
    case 'nef': return fahrzeug(org, (r, f) => arztKreuz(r, f, 22), { kennung: k, textBreite: 16, textY: 19.5 })
    case 'gw_drohne': return fahrzeug(org, (_r, f) => drohneV(cx, 22.5, 0.62, f) + drehfluegler(cx, 27.2, 0.55, f), { kennung: k, textBreite: 30, textY: 18 })
    case 'gw_hundestaffel': return fahrzeug(org, (_r, f) => hund(cx, 25, 0.48, f), { kennung: k, textBreite: 30, textY: 18 })
    case 'einsatzfahrzeug': return fahrzeug(org, () => '', { kennung: k })
    case 'abrollbehaelter':
      return svg(
        `<path d="M8 11 Q26 16 44 11 V33 H8 Z" fill="${fuell}" stroke="${LINIE}" stroke-width="1.6"/>` +
        linie('M8 31 H4.5 V27', LINIE, 1.6) + passText(10.5, 21, k, 26, 7, linienFarbe(org)))
    case 'anhaenger': return fahrzeug(org, () => '', { kennung: k, anhaenger: true, raeder: [cx] })
    case 'hubschrauber':
      return svg(
        `<path d="M9 29 A17 17 0 0 1 43 29 Z" fill="${fuell}" stroke="${LINIE}" stroke-width="1.6"/>` +
        drehfluegler(cx, 34.5, 0.95, LINIE) + passText(cx, 25, k, 22, 6.5, linienFarbe(org), 'middle'))
    case 'wasserfahrzeug':
      return svg(
        `<path d="M9 12 A17 17 0 0 0 43 12 Z" fill="${fuell}" stroke="${LINIE}" stroke-width="1.6"/>` +
        passText(cx, 20, k, 22, 7, linienFarbe(org), 'middle'))
    case 'drohne':
      return svg(
        `<g stroke="#FFFFFF" stroke-width="2.4" stroke-opacity=".9">${drohneV(cx, 16, 1.25, '#FFFFFF')}${drehfluegler(cx, 30, 1.2, '#FFFFFF')}</g>` +
        drohneV(cx, 16, 1.25, LINIE) + drehfluegler(cx, 30, 1.2, LINIE))

    /* --- Einheiten --- */
    case 'drohnentrupp':
      return formation(org, (_r, f) => drohneV(cx, 18.5, 0.85, f) + drehfluegler(cx, 29, 0.8, f), { staerke: 1, kennung: k })
    case 'rettungshund': return formation(org, (_r, f) => hund(cx - 1, 25, 0.82, f), { staerke: 1, kennung: k })
    case 'rettungsdienst': return formation(org, (r, f) => rettKreuz(r, f), { kennung: k })
    case 'aerztliche_versorgung': return formation(org, (r, f) => arztKreuz(r, f), { staerke: 1, kennung: k })
    case 'sanitaetsgruppe': return formation(org, (r, f) => sanKreuz(r, f), { staerke: 1, kennung: k })
    case 'formation_einsatz': return formation(org, (r, f) => betreuungsDach(r, f) + linie(`M${r.x0} 23 H${r.x1}`, f, 1.4), { staerke: 3, kennung: k })
    case 'betreuung': return formation(org, betreuungsDach, { kennung: k })
    case 'psnv': return formation(org, (r, f) => betreuungsDach(r, f) + text(cx, 33, 'PSNV', 6, f), { kennung: k })
    case 'verpflegung': return formation(org, (_r, f) => verpflegung(f), { versorgung: true, kennung: k })
    case 'technik_sicherheit': return formation(org, (_r, f) => schild(f, 6), { kennung: k })
    case 'brandschutz': return formation(org, brand, { kennung: k })

    /* --- Führung --- */
    case 'einsatzleitung':
    case 'befehlsstelle':
      return formation(org, (_r, f) => passText(cx, 29, k, 30, 10, f, 'middle'), { fuehrung: true })
    case 'einsatzleiter':
    case 'lna':
    case 'orgl':
      return person(fuell, '', { fuehrungskraft: true, text: k })
    case 'truppfuehrer': return person(fuell, '', { fuehrungskraft: true, staerke: 1, text: k })
    case 'gruppenfuehrer': return person(fuell, '', { fuehrungskraft: true, staerke: 2, text: k })
    case 'zugfuehrer': return person(fuell, '', { fuehrungskraft: true, staerke: 3, text: k })
    case 'verbandsfuehrer': return person(fuell, '', { fuehrungskraft: true, staerke: 'I', text: k })
    case 'arzt': return person(fuell, linie(`M${cx} ${top} V${unten} M${cx - RAUTE.r} ${RAUTE.cy} H${cx + RAUTE.r} M${cx - 4} ${RAUTE.cy + 6.5} H${cx + 4}`, LINIE, 1.4))
    case 'berater': return person(fuell, '', { sonderfunktion: true, text: k })

    /* --- Stellen und Einrichtungen --- */
    case 'bereitstellungsraum':
      return stelle(fuell, `<path d="M19 18 Q26 20.5 33 18 V28 H19 Z" stroke="${LINIE}" stroke-width="1.4"/>`)
    case 'meldekopf': return stelle(fuell, text(cx, 27.5, 'M', 12))
    case 'leitstelle': return stelle(fuell, passText(cx, 26.5, 'LtS', 18, 9, LINIE, 'middle'), { dach: true })
    case 'landeplatz': return stelle(fuell, drehfluegler(cx, KREIS.cy, 1.1, LINIE))
    case 'drohnen_startplatz': return stelle(fuell, drohneV(cx, 19.5, 0.65, LINIE) + drehfluegler(cx, 28, 0.7, LINIE))
    case 'patientenablage':
      return stelle(fuell, linie(`M${cx - KREIS.r} ${KREIS.cy} H${cx + KREIS.r} M${cx} ${KREIS.cy - KREIS.r} V${KREIS.cy + KREIS.r}`, LINIE, 1.4) + doppelpfeil(cx - 7, cx + 7, KREIS.cy - 5.5, LINIE))
    case 'unfallhilfstelle':
      return stelle(fuell, linie(`M${cx - KREIS.r} ${KREIS.cy} H${cx + KREIS.r} M${cx} ${KREIS.cy - KREIS.r} V${KREIS.cy + KREIS.r}`, LINIE, 1.4), { kennung: k })
    case 'behandlungsplatz':
      return stelle(fuell, linie(`M${cx - KREIS.r} ${KREIS.cy} H${cx + KREIS.r} M${cx} ${KREIS.cy - KREIS.r} V${KREIS.cy + KREIS.r}`, LINIE, 1.4), { dach: true })
    case 'rettungsmittelhalteplatz':
      return stelle(fuell, linie(`M${cx - KREIS.r} ${KREIS.cy} H${cx + KREIS.r} M${cx} ${KREIS.cy - KREIS.r} V${KREIS.cy + KREIS.r}`, LINIE, 1.4) +
        `<circle cx="${cx}" cy="${KREIS.cy}" r="5.2" fill="${fuell}"/>` + speichenrad(cx, KREIS.cy, 5.2, LINIE))
    case 'anlaufstelle':
      return stelle(fuell, doppelpfeil(cx - 7, cx + 7, KREIS.cy - 5, LINIE) +
        linie(`M${cx} ${KREIS.cy - 5} V${KREIS.cy + 4} L${cx - 9} ${KREIS.cy + 8.5} M${cx} ${KREIS.cy + 4} L${cx + 9} ${KREIS.cy + 8.5}`, LINIE, 1.4))
    case 'betreuungsplatz':
      return stelle(fuell, linie(`M${cx - 8.5} ${KREIS.cy + 9} L${cx} ${KREIS.cy - 10} L${cx + 8.5} ${KREIS.cy + 9}`, LINIE, 1.4), { dach: true })
    case 'krankenhaus':
      return gebaeude(fuell, linie('M9 26 H43 M19 19 V33 M33 19 V33', LINIE, 1.4))

    /* --- Gefahren und Schäden --- */
    case 'vermutete_gefahr': return gefahr(text(cx, 23, '?', 16, GEFAHR))
    case 'akute_gefahr': return gefahr(text(cx, 23, '!', 16, GEFAHR))
    case 'gefaehrliche_stoffe': return gefahr(text(cx, 21.5, 'GS', 9.5, GEFAHR))
    case 'explosionsgefahr': return gefahr(text(cx, 21.5, 'Ex', 10, GEFAHR))
    case 'elektrische_energie': return gefahr(linie('M28 8 L22.5 18 H29 L24 26 M24 26 L24.2 22.6 M24 26 L27 24.6', GEFAHR, 1.6))
    case 'gefahr_atomar': return gefahr(text(cx, 22.5, 'A', 12, GEFAHR))
    case 'gefahr_biologisch': return gefahr(text(cx, 22.5, 'B', 12, GEFAHR))
    case 'gefahr_chemisch': return gefahr(text(cx, 22.5, 'C', 12, GEFAHR))
    case 'ueberschwemmung':
      return svg(`<ellipse cx="26" cy="21" rx="21" ry="13" fill="#FFFFFF" fill-opacity=".88" stroke="${MASSNAHME}" stroke-width="1.6" stroke-dasharray="3 2"/>` +
        linie('M12 21 q3.5 -3 7 0 t7 0 t7 0 t7 0', MASSNAHME, 1.5) + text(cx, 30.5, 'W', 8, MASSNAHME))
    case 'entstehungsbrand': return brandStufe(1)
    case 'fortentwickelter_brand': return brandStufe(2)
    case 'vollbrand': return brandStufe(3)
    case 'angeschlagen': return zerstoerung(1)
    case 'teilzerstoert': return zerstoerung(2)
    case 'zerstoert': return zerstoerung(3)
    case 'gesperrt': return svg(linieHof('M26 8 V36 M18 14 L34 30 M34 14 L18 30', LINIE, 1.8))
    case 'schwer_befahrbar': return befahrbarkeit(2)
    case 'nicht_befahrbar': return befahrbarkeit(4)
    case 'erkundung': return massnahme(erkunden(MASSNAHME))

    /* --- Personen (schwarz, 5.8 Farbgebung) --- */
    case 'person': return person(PAPIER, '')
    case 'betroffen': return person(PAPIER, text(cx + 10.5, top + 3.5, 'B', 7.5))
    case 'verletzt': return person(PAPIER, linie(`M${cx} ${top} V${unten}`, LINIE, 1.6))
    case 'tot': return person(PAPIER, linie(`M${cx} ${top} V${unten} M${cx - 8} ${RAUTE.cy - 5} H${cx + 8}`, LINIE, 1.6))
    case 'vermisst':
      return person(PAPIER, linieHof(`M${cx - RAUTE.r - 2.6} ${RAUTE.cy - 2.6} L${cx - 2.6} ${top - 2.6} M${cx + 2.6} ${unten + 2.6} L${cx + RAUTE.r + 2.6} ${RAUTE.cy + 2.6}`, LINIE, 1.4))
    case 'in_wassergefahr':
      return person(PAPIER, linieHof(`M17 5.5 q2.25 -2 4.5 0 t4.5 0 t4.5 0 t4.5 0`, LINIE, 1.3))
    case 'verschuettet': return person(PAPIER, linieHof(`M16 ${top} H36`, LINIE, 1.6))
    case 'gerettet': return person(PAPIER, linieHof(`M16 ${unten} H36`, LINIE, 1.6))
  }

  // Unbekannter Typ: neutrales Fahrzeug mit Kennung
  return fahrzeug(org, () => '', { kennung: k || typ })
}

/** Anzeigename für Liste/Tooltip */
export function symbolLabel(typ: string): string {
  return symbolDef(typ)?.label ?? typ
}
